import { useCallback, useEffect, useRef, useState } from 'react';
import { Award, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { useAuth } from '../../lib/auth';
import { formatDataSP, formatMoeda } from '@fiodecobre/shared';

// PREMIO DO ESTOQUISTA ([DA-868]) — o painel do mes.
//
// O numero grande e' o PREMIO; embaixo, a conta inteira que o produz — nota, faixa, faturamento — e
// a lista de CADA erro com o porque. *"Nao ficarei refem dos seus calculos"*: cada linha da lista
// se confere sem mim (produto, dia, o que o sistema dizia e o que havia).
//
// Admin muda os parametros e gera o titulo; a gerente so ve.

type Faixa = { nota_min: number; percentual_premio: number };
type Config = {
  produtos_por_dia: number; percentual_faturamento: number; peso_avulsa: number; peso_venda_perdida: number;
  estoquista_id: number | null; estoquista_login: string | null; inicio: string;
};
type ErroMes = { quando: string; tipo: string; peso: number; produto: string; detalhe: string };
type Painel = {
  competencia: string; antes_do_inicio: boolean; config: Config; faixas: Faixa[];
  avaliados_sorteio: number; acertos: number; erros_sorteio: number; erros_avulsa: number; erros_venda_perdida: number;
  erros_ponderados: number; nota: number | null; percentual_faixa: number; faturamento: number; valor: number;
  erros: ErroMes[];
  fechamento: { valor: string; titulo_numero: string | null; titulo_status: string | null; gerado_em: string; gatilho: string } | null;
};

const MESES = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const ROTULO_TIPO: Record<string, string> = {
  sorteio: 'Sorteio',
  avulsa: 'Achado',
  venda_perdida_com_estoque: 'Venda perdida (tinha no sistema)',
  venda_perdida_provada: 'Venda perdida (zerado que voltou)',
};
const pct = (n: number | null) => (n === null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`);

export default function PremioEstoque() {
  const dialog = useDialog();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';

  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [p, setP] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const reqId = useRef(0);

  const carregar = useCallback(async () => {
    const req = ++reqId.current;
    try {
      const r = await api.get<Painel>(`/api/estoque/auditoria/mes?ano=${ano}&mes=${mes}`);
      if (req !== reqId.current) return;
      setP(r);
      setErro(null);
    } catch (e: any) {
      if (req === reqId.current) setErro(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel calcular o mes.');
    }
  }, [ano, mes]);

  useEffect(() => { void carregar(); }, [carregar]);

  function mudarMes(delta: number) {
    const d = new Date(ano, mes - 1 + delta, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth() + 1);
  }

  const mesCorrente = ano === hoje.getFullYear() && mes === hoje.getMonth() + 1;

  async function gerarTitulo() {
    if (!p) return;
    const ok = await dialog.confirmar(
      `Gerar o titulo de ${formatMoeda(p.valor)} em Contas a Pagar para ${p.config.estoquista_login ?? 'o estoquista'} (${MESES[mes - 1]}/${ano})?`
      + (mesCorrente ? ' O mes ainda nao fechou — o valor pode mudar ate o fim do mes.' : ''),
      { titulo: 'Gerar titulo do premio' },
    );
    if (!ok) return;
    setGerando(true);
    try {
      const r = await api.post<{ acao: string; valor: number }>('/api/estoque/auditoria/titulo', { ano, mes });
      const msg: Record<string, string> = {
        gerado: 'Titulo gerado em Contas a Pagar.', regerado: 'Titulo anterior cancelado e gerado de novo com o valor atual.',
        ja_gerado: 'O titulo ja existe com este valor — nada mudou.', pago: 'O titulo deste mes ja foi pago — nao se recalcula.',
        sem_premio: 'Sem premio neste mes (faixa 0%) — nenhum titulo gerado.', sem_fornecedor: 'O estoquista nao tem cadastro de favorecido (Fornecedor) vinculado.',
        sem_estoquista: 'Nenhum estoquista configurado.', antes_do_inicio: 'Este mes e anterior ao inicio do premio.',
      };
      await dialog.alerta(msg[r.acao] ?? r.acao, { titulo: 'Titulo do premio' });
      void carregar();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel gerar o titulo.', { titulo: 'Nao gerou', tipo: 'aviso' });
    } finally {
      setGerando(false);
    }
  }

  return (
    <div className="pagina">
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Award size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Premio do Estoquista
        </h2>
        <div className="pagina-acoes" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" className="btn-icon" onClick={() => mudarMes(-1)} title="Mes anterior"><ChevronLeft size={18} /></button>
          <strong style={{ minWidth: 130, textAlign: 'center' }}>{MESES[mes - 1]}/{ano}</strong>
          <button type="button" className="btn-icon" onClick={() => mudarMes(1)} title="Proximo mes"><ChevronRight size={18} /></button>
        </div>
      </div>

      {erro && <div style={{ background: '#f97316', color: '#000', padding: 12, borderRadius: 6, marginBottom: 12, fontWeight: 600 }}>{erro}</div>}
      {!p && !erro && <div style={{padding: 16 }}>Calculando...</div>}

      {p && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1000 }}>
          {p.antes_do_inicio ? (
            <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 16, background: 'var(--panel-bg)' }}>
              O premio comeca a contar em <strong>{formatDataSP(`${p.config.inicio}T12:00:00-03:00`)}</strong>. Este mes e anterior.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 16, background: 'var(--panel-bg)', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={{fontSize: 12 }}>Premio {mesCorrente ? 'ate agora' : 'do mes'} — {p.config.estoquista_login ?? 'sem estoquista'}</div>
                <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{formatMoeda(p.valor)}</div>
                <div style={{fontSize: 12, marginTop: 4 }}>
                  {p.config.percentual_faturamento.toLocaleString('pt-BR')}% de {formatMoeda(p.faturamento)} de faturamento{mesCorrente ? ' (ate hoje)' : ''} × faixa {pct(p.percentual_faixa)}
                </div>
              </div>
              <div>
                <div style={{fontSize: 12 }}>Nota</div>
                <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{pct(p.nota)}</div>
                <div style={{fontSize: 12, marginTop: 4 }}>
                  {p.nota === null
                    ? 'Nenhuma avaliacao ainda neste mes.'
                    : `${p.acertos} acertos ÷ (${p.acertos} + ${p.erros_ponderados} erros ponderados)`}
                </div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><strong>{p.avaliados_sorteio}</strong> sorteados contados: {p.acertos} certos, {p.erros_sorteio} errados (peso 1)</div>
                <div><strong>{p.erros_avulsa}</strong> achados (peso {p.config.peso_avulsa})</div>
                <div><strong>{p.erros_venda_perdida}</strong> vendas perdidas que acusam o estoque (peso {p.config.peso_venda_perdida})</div>
              </div>
            </div>
          )}

          {!p.antes_do_inicio && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {p.fechamento ? (
                <span style={{ fontSize: 13 }}>
                  Titulo <strong>{p.fechamento.titulo_numero ?? '—'}</strong> · {formatMoeda(Number(p.fechamento.valor))} ·{' '}
                  {p.fechamento.titulo_status ?? 'sem titulo'} · gerado {formatDataSP(p.fechamento.gerado_em)} ({p.fechamento.gatilho})
                  {p.fechamento.titulo_status === 'pendente' && Math.abs(Number(p.fechamento.valor) - p.valor) > 0.01 && (
                    <strong style={{ color: 'var(--num-destaque)' }}> — a conta de hoje da {formatMoeda(p.valor)}</strong>
                  )}
                </span>
              ) : (
                <span style={{fontSize: 13 }}>
                  Sem titulo ainda. O sistema gera sozinho no 1o dia util do mes seguinte (mesmo interruptor das comissoes).
                </span>
              )}
              {ehAdmin && (
                <button type="button" className="btn-primario" onClick={() => void gerarTitulo()} disabled={gerando} style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                  {gerando ? 'Gerando...' : p.fechamento ? 'Regerar titulo agora' : 'Gerar titulo agora'}
                </button>
              )}
            </div>
          )}

          {!p.antes_do_inicio && (
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Erros do mes {p.erros.length > 0 ? `(${p.erros.length})` : ''}
              </div>
              <div className="overflow-x-auto">
                <table className="tabela" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Dia</th>
                      <th style={{ textAlign: 'left' }}>Tipo</th>
                      <th style={{ textAlign: 'right' }}>Peso</th>
                      <th style={{ textAlign: 'left' }}>Produto</th>
                      <th style={{ textAlign: 'left' }}>O que aconteceu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.erros.length === 0 && (
                      <tr><td colSpan={5} style={{padding: 12 }}>Nenhum erro neste mes.</td></tr>
                    )}
                    {p.erros.map((e, i) => (
                      <tr key={`${e.quando}-${e.tipo}-${i}`}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDataSP(`${e.quando}T12:00:00-03:00`)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{ROTULO_TIPO[e.tipo] ?? e.tipo}</td>
                        <td style={{ textAlign: 'right' }}>{e.peso}</td>
                        <td>{e.produto}</td>
                        <td>{e.detalhe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ehAdmin && <Configuracao inicial={p} onSalvo={() => void carregar()} />}
        </div>
      )}
    </div>
  );
}

function Configuracao({ inicial, onSalvo }: { inicial: Painel; onSalvo: () => void }) {
  const dialog = useDialog();
  const [porDia, setPorDia] = useState(String(inicial.config.produtos_por_dia));
  const [percentual, setPercentual] = useState(String(inicial.config.percentual_faturamento).replace('.', ','));
  const [pesoAvulsa, setPesoAvulsa] = useState(String(inicial.config.peso_avulsa));
  const [pesoVp, setPesoVp] = useState(String(inicial.config.peso_venda_perdida));
  const [faixas, setFaixas] = useState(() => inicial.faixas.map((f) => ({ nota: String(f.nota_min).replace('.', ','), premio: String(f.percentual_premio).replace('.', ',') })));
  const [salvando, setSalvando] = useState(false);
  const n = (s: string) => Number(String(s).replace(',', '.'));

  async function salvar() {
    const corpo = {
      produtos_por_dia: n(porDia), percentual_faturamento: n(percentual),
      peso_avulsa: n(pesoAvulsa), peso_venda_perdida: n(pesoVp),
      faixas: faixas.map((f) => ({ nota_min: n(f.nota), percentual_premio: n(f.premio) })),
    };
    const ruim = [corpo.produtos_por_dia, corpo.percentual_faturamento, corpo.peso_avulsa, corpo.peso_venda_perdida,
      ...corpo.faixas.flatMap((f) => [f.nota_min, f.percentual_premio])].some((x) => !Number.isFinite(x));
    if (ruim) { await dialog.alerta('Algum campo nao e numero.', { titulo: 'Confira', tipo: 'aviso' }); return; }
    setSalvando(true);
    try {
      await api.put('/api/estoque/auditoria/config', corpo);
      dialog.toast('Configuracao do premio salva');
      onSalvo();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel salvar.', { titulo: 'Nao salvou', tipo: 'aviso' });
    } finally {
      setSalvando(false);
    }
  }

  const campo = (rot: string, v: string, set: (s: string) => void, sufixo?: string) => (
    <div>
      <label style={{fontSize: 11, display: 'block' }}>{rot}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={v} onChange={(e) => set(e.target.value)} inputMode="decimal" className="cel-num" style={{ width: 90, textAlign: 'right' }} />
        {sufixo && <span style={{fontSize: 12 }}>{sufixo}</span>}
      </div>
    </div>
  );

  return (
    <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 14, background: 'var(--panel-bg)' }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Configuracao</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        {campo('Produtos sorteados por dia', porDia, setPorDia)}
        {campo('Premio cheio', percentual, setPercentual, '% do faturamento')}
        {campo('Peso do achado', pesoAvulsa, setPesoAvulsa, 'erros')}
        {campo('Peso da venda perdida', pesoVp, setPesoVp, 'erros')}
      </div>
      <div style={{fontSize: 12, marginBottom: 6 }}>Faixas: com nota igual ou acima de, recebe esta parte do premio cheio. Vale a maior faixa alcancada.</div>
      <table className="tabela" style={{ width: 'auto' }}>
        <thead>
          <tr><th style={{ textAlign: 'right' }}>Nota a partir de</th><th style={{ textAlign: 'right' }}>% do premio</th><th /></tr>
        </thead>
        <tbody>
          {faixas.map((f, i) => (
            <tr key={i}>
              <td><input value={f.nota} onChange={(e) => setFaixas((fs) => fs.map((x, k) => (k === i ? { ...x, nota: e.target.value } : x)))} inputMode="decimal" className="cel-num" style={{ width: 90, textAlign: 'right' }} /></td>
              <td><input value={f.premio} onChange={(e) => setFaixas((fs) => fs.map((x, k) => (k === i ? { ...x, premio: e.target.value } : x)))} inputMode="decimal" className="cel-num" style={{ width: 90, textAlign: 'right' }} /></td>
              <td>
                <button type="button" title="Remover faixa" onClick={() => setFaixas((fs) => fs.filter((_, k) => k !== i))} disabled={faixas.length <= 1}
                  style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit' }}>
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="button" className="btn-secundario" onClick={() => setFaixas((fs) => [...fs, { nota: '', premio: '' }])}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <Plus size={14} /> Adicionar faixa
        </button>
        <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={salvando} style={{ whiteSpace: 'nowrap' }}>
          {salvando ? 'Salvando...' : 'Salvar configuracao'}
        </button>
      </div>
    </div>
  );
}
